import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@store/types';

import type { GuideSurfaceId } from '../constants/featureLinks';

export type GuideDrawerTarget = {
  slug: string;
  surfaceId: GuideSurfaceId;
  /** Section fragment, scoped to the drawer's own scroll container. */
  hash?: string;
};

type HistoryEntry = { slug: string; hash: string | null; scrollTop: number | null };
export type GuideDrawerState = {
  open: boolean;
  slug: string | null;
  surfaceId: GuideSurfaceId | null;
  hash: string | null;
  scrollTop: number | null;
  past: HistoryEntry[];
  future: HistoryEntry[];
};

declare module '@store/types' {
  interface DynamicFeatureState {
    guideDrawer?: GuideDrawerState;
  }
}

const initialState: GuideDrawerState = {
  open: false,
  slug: null,
  surfaceId: null,
  hash: null,
  scrollTop: null,
  past: [],
  future: [],
};

function currentEntry(state: GuideDrawerState, scrollTop: number): HistoryEntry | undefined {
  return state.open && state.slug ? { slug: state.slug, hash: state.hash, scrollTop } : undefined;
}

export const guideDrawerSlice = createSlice({
  name: 'guideDrawer',
  initialState,
  reducers: {
    // An application link starts a fresh reading session. No history is persisted or shared across tabs.
    openGuideDrawer: (_state, { payload }: PayloadAction<GuideDrawerTarget>) => ({
      ...initialState,
      open: true,
      slug: payload.slug,
      surfaceId: payload.surfaceId,
      hash: payload.hash || null,
    }),
    navigateGuideDrawer: (
      state,
      { payload }: PayloadAction<{ slug: string; hash?: string; scrollTop: number }>,
    ) => {
      const current = currentEntry(state, payload.scrollTop);
      if (!current || (state.slug === payload.slug && state.hash === (payload.hash || null)))
        return;
      state.past.push(current);
      state.future = [];
      state.slug = payload.slug;
      state.hash = payload.hash || null;
      state.scrollTop = null;
    },
    goBackInGuideDrawer: (state, { payload: scrollTop }: PayloadAction<number>) => {
      const current = currentEntry(state, scrollTop);
      const previous = state.past.at(-1);
      if (!current || !previous) return;
      state.past.pop();
      state.future.push(current);
      Object.assign(state, previous);
    },
    goForwardInGuideDrawer: (state, { payload: scrollTop }: PayloadAction<number>) => {
      const current = currentEntry(state, scrollTop);
      const next = state.future.at(-1);
      if (!current || !next) return;
      state.future.pop();
      state.past.push(current);
      Object.assign(state, next);
    },
    closeGuideDrawer: () => initialState,
  },
});

export const {
  openGuideDrawer,
  navigateGuideDrawer,
  goBackInGuideDrawer,
  goForwardInGuideDrawer,
  closeGuideDrawer,
} = guideDrawerSlice.actions;
export const guideDrawerReducer = guideDrawerSlice.reducer;
export const selectGuideDrawer = (state: RootState): GuideDrawerState =>
  state.guideDrawer ?? initialState;
